import { ReactElement, useEffect, useState } from 'react';

import { getDownloadURL, ref as firebaseStorageRef } from 'firebase/storage';

import Avatar from '@mui/material/Avatar';

import GroupsIcon from '@mui/icons-material/Groups';

import { storage } from '../../firebase';
import Membership from '../../types/Membership';
import Team from '../../types/Team';
import GratiDirectoryItem from '../common/GratiDirectoryItem';

import { formatManagerNames, getTeamManagers } from './teamListItemUtils';

interface TeamListItemProps {
  team: Team;
  /** All team memberships (members + admins). Managers are filtered from this list. */
  memberships?: Membership[];
  isLeagueNameDisplayed?: boolean;
  isOrgNameDisplayed?: boolean;
  isDividerDisplayed?: boolean;
  isClickable?: boolean;
  /** Actions to render on the far right */
  renderActions?: ReactElement;
  /** Whether the actions menu is open (highlights the row) */
  isActionsOpen?: boolean;
  /** Preview URL for logo (used during create flow before upload) */
  previewLogoUrl?: string | null;
}

export default function TeamListItem(props: TeamListItemProps): ReactElement {
  const {
    team,
    memberships,
    isOrgNameDisplayed = false,
    isLeagueNameDisplayed = false,
    isClickable = false,
    isDividerDisplayed = false,
    renderActions,
    isActionsOpen = false,
    previewLogoUrl,
  } = props;

  const [logoUrl, setLogoUrl] = useState<string | null>(null);

  // Fetch logo from Firebase Storage if team has one
  useEffect(() => {
    if (team.logo) {
      const storageRef = firebaseStorageRef(
        storage,
        `org/${team.orgId}/league/${team.leagueId}/team/${team.itemId}/orgMemberTeamManagerFile/${team.logo}`
      );
      getDownloadURL(storageRef)
        .then((url) => setLogoUrl(url))
        .catch(() => setLogoUrl(null));
    } else {
      setLogoUrl(null);
    }
  }, [team.logo, team.orgId, team.leagueId, team.itemId]);

  const managers = memberships ? getTeamManagers(memberships, team.itemId) : [];
  const managerNames = formatManagerNames(managers);

  const StyledAvatar = (): ReactElement => {
    // Use preview URL (for create flow) or fetched URL from storage
    const displayLogoUrl = previewLogoUrl || logoUrl;
    return (
      <Avatar
        alt={team.name}
        src={displayLogoUrl || undefined}
        sx={{
          bgcolor: 'action.selected',
          color: 'text.primary',
          // White background for transparent PNGs
        }}
      >
        <GroupsIcon />
      </Avatar>
    );
  };

  const getSecondaryText = (): string | undefined => {
    // Show description if available, otherwise show org/league context
    if (team.description) {
      return team.description;
    }

    const parts: string[] = [];
    if (isOrgNameDisplayed && team.orgName) {
      parts.push(team.orgName);
    }
    if (isLeagueNameDisplayed && team.leagueName) {
      parts.push(team.leagueName);
    }
    return parts.length > 0 ? parts.join(' \u2022 ') : undefined;
  };

  const getTertiaryText = (): string | undefined => {
    // Show manager names if we have memberships data
    if (!managerNames) return undefined;
    const label = managers.length > 1 ? 'Managers' : 'Manager';
    return `${label}: ${managerNames}`;
  };

  return (
    <GratiDirectoryItem
      to={isClickable ? `/team/${team.orgId}/${team.itemId}` : undefined}
      divider={isDividerDisplayed}
      className={team.status}
      isActionOpen={isActionsOpen}
      slots={{
        color: team.color,
        icon: <StyledAvatar />,
        primary: team.name || 'Team',
        secondary: getSecondaryText(),
        tertiary: getTertiaryText(),
        action: renderActions,
      }}
    />
  );
}
